import React, { createContext, useContext, ReactNode } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';

/**
 * AnimationContextValue
 * Provides global animation settings and preferences
 */
interface AnimationContextValue {
  /** Whether user prefers reduced motion */
  prefersReducedMotion: boolean;
  /** Whether animations should be enabled */
  animationsEnabled: boolean;
  /** Whether component is visible (for pausing animations when tab hidden) */
  isVisible: boolean;
}

const AnimationContext = createContext<AnimationContextValue | undefined>(undefined);

/**
 * AnimationProvider
 * Provides animation context to all child components
 * Handles reduced motion detection and visibility state
 */
interface AnimationProviderProps {
  children: ReactNode;
}

export const AnimationProvider: React.FC<AnimationProviderProps> = ({ children }) => {
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = React.useState(!document.hidden);

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const animationsEnabled = !prefersReducedMotion && isVisible;

  const value: AnimationContextValue = {
    prefersReducedMotion,
    animationsEnabled,
    isVisible,
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};

/**
 * useAnimationContext hook
 * Access animation context values
 * 
 * @returns AnimationContextValue
 * @throws Error if used outside AnimationProvider
 * 
 * @example
 * const { animationsEnabled } = useAnimationContext();
 * <motion.div animate={animationsEnabled ? { opacity: 1 } : {}} />
 */
export const useAnimationContext = (): AnimationContextValue => {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimationContext must be used within an AnimationProvider');
  }
  return context;
};

